import { useMutation, useQueryClient } from "@tanstack/react-query";

import { toApiError } from "../../lib/apiError";
import { parseImportDiagnostics } from "../../lib/importDiagnostics";
import { useApiClient } from "../useApiClient";
import type { components } from "../schema";
import { ImportDiagnosticsError } from "./useValidateImport";

type SetStatementTimezoneResponse = components["schemas"]["SetStatementTimezoneResponse"];

/**
 * The zone is already in force and cannot move: the import won the race.
 *
 * CARRIES THE ZONE FROM THE RESPONSE BODY, which is why this error type exists
 * rather than a plain message. `_statement_timezone_already_set_handler` puts
 * `statement_timezone` and `requested_timezone` on the 409 precisely so the page
 * can re-render the decision; a second GET to find out what won would race the
 * same way the first request did, and there is no endpoint that reports it.
 *
 * Distinct from the soft-deleted-account 409, which carries only `detail` --
 * that one is a generic conflict and is surfaced as a message.
 */
export class StatementTimezoneAlreadySetError extends Error {
  readonly inForce: string;
  readonly requested: string;

  constructor(message: string, inForce: string, requested: string) {
    super(message);
    this.name = "StatementTimezoneAlreadySetError";
    this.inForce = inForce;
    this.requested = requested;
  }
}

export type SetStatementTimezoneVariables = {
  accountId: string;
  /** An IANA name. There is deliberately no default anywhere in this path. */
  timezone: string;
};

function conflictZones(error: unknown): { inForce: string; requested: string } | null {
  if (typeof error !== "object" || error === null) return null;
  const body = error as { statement_timezone?: unknown; requested_timezone?: unknown };
  if (typeof body.statement_timezone !== "string") return null;
  return {
    inForce: body.statement_timezone,
    requested: typeof body.requested_timezone === "string" ? body.requested_timezone : "",
  };
}

/**
 * Set the timezone an account's statements display times in (ADR 0039 D9.2).
 *
 * The outcomes the picker has to tell apart, all from the same call:
 *
 * * **200** -- set, or changed, or already on the requested zone.
 *   `was_set_by_this_request` distinguishes a write from a no-op, and a no-op
 *   writes no audit entry server-side.
 * * **409 with `statement_timezone`** -- imported fills exist, so the zone is
 *   locked. Read-only from there.
 * * **400 with `diagnostics`** -- a LEGACY account (imported fills, no zone).
 *   Rendered through the SAME remedy catalogue the import path uses, which is
 *   why it is rethrown as `ImportDiagnosticsError` rather than a message: there
 *   is no picker for that state, only the re-localisation guidance.
 * * **503 with `diagnostics`** -- lock contention, `retry_import`. Same path.
 * * anything else -- a message for the field.
 */
export function useSetStatementTimezone() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<SetStatementTimezoneResponse, Error, SetStatementTimezoneVariables>({
    mutationFn: async ({ accountId, timezone }) => {
      const { data, error, response } = await client.PUT(
        "/api/accounts/{account_id}/statement-timezone",
        {
          params: { path: { account_id: accountId } },
          body: { timezone },
        },
      );
      if (error !== undefined) {
        const message = toApiError(error, "Failed to set the statement timezone.");
        if (response.status === 409) {
          const zones = conflictZones(error);
          if (zones !== null) {
            throw new StatementTimezoneAlreadySetError(message, zones.inForce, zones.requested);
          }
          throw new Error(message);
        }
        const diagnostics = parseImportDiagnostics(error);
        if (diagnostics.length > 0) {
          throw new ImportDiagnosticsError(message, diagnostics);
        }
        throw new Error(message);
      }
      // Nothing on the accounts list carries the zone today, but the list is
      // what every account-scoped screen reads, so an account whose state just
      // changed is refetched rather than left to a page reload.
      await queryClient.invalidateQueries({ queryKey: ["accounts"] });
      return data;
    },
  });
}
